import classNames from 'classnames';
import moment from 'moment';
import React, { ReactNode, useEffect, useState } from 'react';
import './Countdown.pcss';

export type ParsedTimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

type Props = {
  expiresAt: string;
  parsedTimeLeft: ParsedTimeLeft | undefined;
  setParsedTimeLeft: (parsedTimeLeft: ParsedTimeLeft | undefined) => void;
  customRenderer?: ReactNode;
  previousExpiresAt?: string;
  withSeconds?: boolean;
  css?: {
    countdown?: string;
    countdownDays?: string;
    countdownDaysText?: string;
    countdownHoursAndMinutes?: string;
    countdownSeconds?: string;
  };
};

export const Countdown = ({
  expiresAt,
  parsedTimeLeft,
  setParsedTimeLeft,
  previousExpiresAt,
  withSeconds = true,
  css,
  customRenderer,
}: Props) => {
  const [isAnimationShown, setIsAnimationShown] = useState(false);
  const [hoursAddedInAnimation, setHoursAddedInAnimation] = useState(0);

  useEffect(() => {
    const isAnimating = previousExpiresAt && !isAnimationShown;

    const updateRes = () => {
      const now = moment();
      if (previousExpiresAt && !isAnimationShown) {
        const momentPreviousExpiresAt = moment(previousExpiresAt);
        const momentExpirationDurationDiff = moment
          .duration(moment(expiresAt).diff(momentPreviousExpiresAt))
          .asHours();
        if (
          momentExpirationDurationDiff <= 0 ||
          hoursAddedInAnimation >= momentExpirationDurationDiff
        ) {
          setIsAnimationShown(true);
          return;
        }
        const newHoursValue = hoursAddedInAnimation + 1;
        const durationPrevious = moment.duration(
          momentPreviousExpiresAt.add(newHoursValue, 'hours').diff(now),
        );
        const newParsedTime = {
          days: durationPrevious.days(),
          hours: durationPrevious.hours(),
          minutes: durationPrevious.minutes(),
          seconds: durationPrevious.seconds(),
        };
        setParsedTimeLeft(
          durationPrevious.as('seconds') > 0 ? newParsedTime : undefined,
        );
        setHoursAddedInAnimation(newHoursValue);
      } else {
        const momentExpiresAt = moment(expiresAt);
        const duration = moment.duration(momentExpiresAt.diff(now));
        const newParsedTime = {
          days: duration.days(),
          hours: duration.hours(),
          minutes: duration.minutes(),
          seconds: duration.seconds(),
        };
        setParsedTimeLeft(
          duration.as('seconds') > 0 ? newParsedTime : undefined,
        );
      }
    };
    const interval = setInterval(updateRes, isAnimating ? 90 : 1000);
    return () => {
      clearInterval(interval);
    };
  }, [expiresAt, isAnimationShown, hoursAddedInAnimation]);

  const days =
    parsedTimeLeft?.days === 0 || parsedTimeLeft?.days === undefined
      ? undefined
      : parsedTimeLeft?.days === 1
      ? ' day'
      : ' days';

  if (!parsedTimeLeft) return null;

  return (
    <div className={classNames('countdown', css?.countdown)}>
      {customRenderer ? (
        customRenderer
      ) : (
        <>
          {days && (
            <div className={classNames('countdown__days', css?.countdownDays)}>
              {parsedTimeLeft?.days}
              <span
                className={classNames(
                  'countdown__days-text',
                  css?.countdownDaysText,
                )}
              >
                {days}
              </span>
            </div>
          )}
          <div
            className={classNames(
              'countdown__time',
              css?.countdownHoursAndMinutes,
            )}
          >
            {parsedTimeLeft.hours.toString().padStart(2, '0')}
          </div>
          <div
            className={classNames(
              'countdown__colon',
              css?.countdownHoursAndMinutes,
            )}
          >
            :
          </div>
          <div
            className={classNames(
              'countdown__time',
              css?.countdownHoursAndMinutes,
            )}
          >
            {parsedTimeLeft.minutes.toString().padStart(2, '0')}
          </div>
          {withSeconds && (
            <>
              <div
                className={classNames(
                  'countdown__colon',
                  css?.countdownSeconds,
                )}
              >
                :
              </div>
              <div
                className={classNames(
                  'countdown__time-seconds',
                  css?.countdownSeconds,
                )}
              >
                {parsedTimeLeft.seconds.toString().padStart(2, '0')}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
